const FPS_HEADER_CANDIDATES = [
	"x-amz-meta-fps",
	"x-amz-meta-framerate",
	"x-amz-meta-frame-rate",
	"x-amz-meta-video-fps",
	"x-amz-meta-video_fps"
];

export const DEFAULT_VIDEO_FPS = 30;

/**
 * Snap a time value to the nearest frame boundary for the given FPS.
 * E.g. at 30 FPS, 1.5123s → frame 45 → 45/30 = 1.5000s.
 * This ensures clip segment boundaries align to actual video frames
 * for precise FFmpeg cuts.
 */
export function snapTimeToFrame(timeSeconds: number, fps: number): number {
	if (fps <= 0) return timeSeconds;
	const frameIndex = Math.round(timeSeconds * fps);
	return frameIndex / fps;
}

/**
 * Convert seconds to an HH:MM:SS:FF SMPTE timecode using the given FPS.
 *
 * The frame component (FF) is derived from the exact FPS, while the
 * nominal (rounded) FPS is used as the frame-per-second base for the
 * timecode digits — matching MediaConvert's non-drop-frame convention.
 *
 * Examples (30 FPS):
 *   0.0     → "00:00:00:00"
 *   1.5     → "00:00:01:15"
 *   65.5333 → "00:01:05:16"
 */
export function secondsToTimecode(seconds: number, fps: number): string {
	const safeFps = fps > 0 ? fps : DEFAULT_VIDEO_FPS;
	// Total frames using exact FPS for precision
	const totalFrames = Math.round(Math.max(0, seconds) * safeFps);
	// Nominal (integer) frame rate for timecode digits (e.g., 29.97 → 30)
	const nominalFps = Math.round(safeFps);

	const ff = totalFrames % nominalFps;
	const totalSecs = Math.floor(totalFrames / nominalFps);
	const ss = totalSecs % 60;
	const mm = Math.floor(totalSecs / 60) % 60;
	const hh = Math.floor(totalSecs / 3600);

	return (
		String(hh).padStart(2, "0") +
		":" +
		String(mm).padStart(2, "0") +
		":" +
		String(ss).padStart(2, "0") +
		":" +
		String(ff).padStart(2, "0")
	);
}

export function parseFpsValue(value: string | null | undefined): number | null {
	if (!value) return null;
	const trimmed = value.trim();
	if (!trimmed) return null;

	// Accept simple number values like "30" or "29.97"
	const asNumber = Number(trimmed);
	if (Number.isFinite(asNumber) && asNumber > 0) {
		return asNumber;
	}

	// Accept ratio values like "30000/1001"
	if (trimmed.includes("/")) {
		const [numRaw, denRaw] = trimmed.split("/");
		const num = Number(numRaw);
		const den = Number(denRaw);
		if (Number.isFinite(num) && Number.isFinite(den) && den > 0) {
			const fps = num / den;
			return fps > 0 ? fps : null;
		}
	}
	return null;
}

export async function resolveFpsFromS3Url(url: string): Promise<number | null> {
	if (!url) return null;
	try {
		const response = await fetch(url, {
			method: "HEAD"
		});
		if (!response.ok) {
			return null;
		}

		for (const headerName of FPS_HEADER_CANDIDATES) {
			const parsed = parseFpsValue(response.headers.get(headerName));
			if (parsed) {
				return parsed;
			}
		}
	} catch (error) {
		console.warn("Failed to resolve FPS from S3 metadata", error);
	}
	return null;
}
